/**
 * DAS2 Beneficiary Form Controller (EPIC-023 / 23.5 — §12).
 *
 * Modale `<dialog id="das2-completion-modal">` ouverte par `das2-list`
 * via l'événement `das2:openBeneficiaryForm` (CustomEvent `detail.beneficiary`).
 *
 * Pilote la complétion / validation / exclusion d'un bénéficiaire DAS2 :
 *   - validation client : SIRET 14 chiffres, code postal 5 chiffres,
 *     forme juridique non vide.
 *   - PATCH `/das2/beneficiaries/{id}` :
 *     - `{action: 'override', overrides: {…}}` si l'utilisateur a saisi
 *       SIRET + adresse + CP + ville + forme juridique → bascule
 *       automatiquement en `validated` côté serveur si éligible.
 *     - `{action: 'exclude'}` sur le bouton « Exclure ».
 *   - dispatch `das2:beneficiaryUpdated` à la sortie de succès pour
 *     forcer un rechargement côté `das2-list`.
 *
 * Le serveur reste l'autorité d'éligibilité (forme juridique, pays,
 * seuil 2 400 € TTC). Aucune décision métier ici.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "../services/api.js";

const SIRET_PATTERN = /^\d{14}$/;
const POSTAL_CODE_PATTERN = /^\d{5}$/;

export default class Das2BeneficiaryFormController extends BaseController {
  static targets = [
    "dialog",
    "supplierName",
    "siret",
    "siretError",
    "address",
    "postalCode",
    "postalCodeError",
    "city",
    "legalForm",
    "legalFormError",
    "formError",
    "excludeButton",
    "submitButton",
  ];

  connect() {
    this._currentBeneficiaryId = null;
    this._openHandler = (event) => this.open(event);
    window.addEventListener("das2:openBeneficiaryForm", this._openHandler);
  }

  disconnect() {
    if (this._openHandler) {
      window.removeEventListener("das2:openBeneficiaryForm", this._openHandler);
    }
  }

  open(event) {
    const beneficiary = event?.detail?.beneficiary;
    if (!beneficiary || !beneficiary.id) return;

    this._currentBeneficiaryId = beneficiary.id;
    this._clearErrors();

    if (this.hasSupplierNameTarget) {
      this.supplierNameTarget.replaceChildren();
      const strong = document.createElement("strong");
      strong.textContent = beneficiary.supplierName ?? "—";
      this.supplierNameTarget.append(strong);
    }

    const overrides = beneficiary.overrides ?? {};
    if (this.hasSiretTarget) {
      this.siretTarget.value = (overrides.siret ?? beneficiary.siret ?? "").replace(/\D/g, "");
    }
    if (this.hasAddressTarget) {
      this.addressTarget.value = overrides.address ?? beneficiary.address ?? "";
    }
    if (this.hasPostalCodeTarget) {
      this.postalCodeTarget.value = overrides.postalCode ?? beneficiary.postalCode ?? "";
    }
    if (this.hasCityTarget) {
      this.cityTarget.value = overrides.city ?? beneficiary.city ?? "";
    }
    if (this.hasLegalFormTarget) {
      this.legalFormTarget.value = overrides.legalForm ?? beneficiary.legalForm ?? "";
    }

    if (this.hasDialogTarget && typeof this.dialogTarget.showModal === "function") {
      this.dialogTarget.showModal();
    } else if (this.hasDialogTarget) {
      this.dialogTarget.setAttribute("open", "open");
    }
  }

  cancel() {
    this._closeDialog();
  }

  async submit() {
    if (!this._currentBeneficiaryId) return;
    if (!this._validateClient()) return;

    const overrides = {
      siret: this.siretTarget.value.trim(),
      address: this.addressTarget.value.trim(),
      postalCode: this.postalCodeTarget.value.trim(),
      city: this.cityTarget.value.trim(),
      legalForm: this.legalFormTarget.value.trim(),
    };

    this._toggleButtons(true);
    try {
      await api.das2.updateBeneficiary(this._currentBeneficiaryId, "override", overrides);
      window.dispatchEvent(new CustomEvent("das2:beneficiaryUpdated", {
        detail: { beneficiaryId: this._currentBeneficiaryId },
      }));
      this._closeDialog();
      this.showSuccess("Bénéficiaire mis à jour.");
    } catch (error) {
      this._renderServerError(error);
    } finally {
      this._toggleButtons(false);
    }
  }

  async exclude() {
    if (!this._currentBeneficiaryId) return;

    const confirmed = await this.confirm(
      "Exclure ce bénéficiaire de la déclaration DAS2 ? Vous pourrez le réinclure ensuite.",
      { confirmLabel: "Exclure", cancelLabel: "Annuler" },
    );
    if (!confirmed) return;

    this._toggleButtons(true);
    try {
      await api.das2.updateBeneficiary(this._currentBeneficiaryId, "exclude");
      window.dispatchEvent(new CustomEvent("das2:beneficiaryUpdated", {
        detail: { beneficiaryId: this._currentBeneficiaryId },
      }));
      this._closeDialog();
      this.showSuccess("Bénéficiaire exclu.");
    } catch (error) {
      this._renderServerError(error);
    } finally {
      this._toggleButtons(false);
    }
  }

  _validateClient() {
    this._clearErrors();
    let ok = true;

    const siret = this.siretTarget.value.trim();
    if (!SIRET_PATTERN.test(siret)) {
      this._showFieldError(this.siretErrorTarget, "SIRET : 14 chiffres attendus.");
      ok = false;
    }

    const postalCode = this.postalCodeTarget.value.trim();
    if (!POSTAL_CODE_PATTERN.test(postalCode)) {
      this._showFieldError(this.postalCodeErrorTarget, "Code postal : 5 chiffres attendus.");
      ok = false;
    }

    if (this.legalFormTarget.value === "") {
      this._showFieldError(this.legalFormErrorTarget, "Sélectionnez une forme juridique.");
      ok = false;
    }

    return ok;
  }

  _showFieldError(target, message) {
    if (!target) return;
    target.textContent = message;
    target.hidden = false;
  }

  _clearErrors() {
    [
      this.hasSiretErrorTarget ? this.siretErrorTarget : null,
      this.hasPostalCodeErrorTarget ? this.postalCodeErrorTarget : null,
      this.hasLegalFormErrorTarget ? this.legalFormErrorTarget : null,
      this.hasFormErrorTarget ? this.formErrorTarget : null,
    ].forEach((el) => {
      if (!el) return;
      el.textContent = "";
      el.hidden = true;
    });
  }

  _renderServerError(error) {
    if (!(error instanceof APIError)) {
      this._showFieldError(this.formErrorTarget, "Erreur réseau, réessayez.");
      return;
    }
    if (error.status === 422 && error.data?.error?.details) {
      const details = error.data.error.details;
      if (details.siret) this._showFieldError(this.siretErrorTarget, details.siret);
      if (details.postalCode) this._showFieldError(this.postalCodeErrorTarget, details.postalCode);
      if (details.legalForm) this._showFieldError(this.legalFormErrorTarget, details.legalForm);
      return;
    }
    if (error.status === 423) {
      this._showFieldError(this.formErrorTarget, "Cette déclaration est verrouillée (déjà soumise).");
      return;
    }
    this._showFieldError(this.formErrorTarget, error.message || "Échec de la mise à jour.");
  }

  _toggleButtons(busy) {
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = busy;
    if (this.hasExcludeButtonTarget) this.excludeButtonTarget.disabled = busy;
  }

  _closeDialog() {
    if (!this.hasDialogTarget) return;
    if (typeof this.dialogTarget.close === "function") {
      this.dialogTarget.close();
    } else {
      this.dialogTarget.removeAttribute("open");
    }
    this._currentBeneficiaryId = null;
  }
}
